import { PushNotifications, type Token, type PushNotificationSchema } from '@capacitor/push-notifications';
import { isNative, platform } from './platform';
import { api } from './api-client';
import { STORAGE_KEYS, getItem, removeItem, setItem } from './storage';

export type PushOnNotification = (n: PushNotificationSchema) => void;

export async function isPushOptedIn(): Promise<boolean> {
  try {
    const v = await getItem(STORAGE_KEYS.PUSH_OPTIN);
    return v === '1';
  } catch {
    return false;
  }
}

export async function storedPushToken(): Promise<string | null> {
  try {
    return await getItem(STORAGE_KEYS.PUSH_TOKEN);
  } catch {
    return null;
  }
}

/**
 * Request push permission + register for remote notifications.
 * Returns the device token on success, null on denial / unsupported.
 *
 * On success we also POST the token to the backend so it can route
 * server-side pushes. Backend route currently absent — silently skip
 * (token still stored locally so re-sync later is cheap).
 */
export async function registerPush(onNotify?: PushOnNotification): Promise<string | null> {
  if (!isNative()) return null;

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') {
    await setItem(STORAGE_KEYS.PUSH_OPTIN, '0');
    return null;
  }
  await setItem(STORAGE_KEYS.PUSH_OPTIN, '1');

  return new Promise<string | null>((resolve) => {
    let settled = false;
    const done = (token: string | null) => {
      if (settled) return;
      settled = true;
      resolve(token);
    };

    void PushNotifications.addListener('registration', (t: Token) => {
      void setItem(STORAGE_KEYS.PUSH_TOKEN, t.value);
      void syncTokenToBackend(t.value);
      done(t.value);
    });
    void PushNotifications.addListener('registrationError', () => done(null));
    if (onNotify) {
      void PushNotifications.addListener('pushNotificationReceived', onNotify);
    }

    void PushNotifications.register();
    // Safety net: resolve null after 10s so callers never hang.
    setTimeout(() => done(null), 10_000);
  });
}

/**
 * Best-effort sync: backend will likely 404 until the push-token endpoint
 * ships. We swallow the error — token is kept locally and can retry later.
 */
async function syncTokenToBackend(token: string): Promise<void> {
  try {
    await api.post('/users/me/push-tokens', { token, platform: platform() });
  } catch {
    void 0;
  }
}

export async function unregisterPush(): Promise<void> {
  if (!isNative()) return;
  await removeItem(STORAGE_KEYS.PUSH_TOKEN);
  await setItem(STORAGE_KEYS.PUSH_OPTIN, '0');
  try {
    await PushNotifications.removeAllListeners();
  } catch {
    void 0;
  }
}
